import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig, AxiosHeaders } from 'axios';

const isDev =
  typeof import.meta !== 'undefined' &&
  typeof (import.meta as any).env !== 'undefined' &&
  ((import.meta as any).env.MODE !== 'production');

// Get API base URL from environment or use relative path for production
const getApiBaseUrl = () => {
  if (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_BASE_URL) {
    return (import.meta as any).env.VITE_API_BASE_URL;
  }
  // In production, use relative path (same domain) - now using simplified paths
  return isDev ? 'http://localhost:5555' : '/api';
};

export interface ApiResponse<T = any> {
  data: T;
  message?: string;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, any>;
}

export class ApiClient {
  private client: AxiosInstance;
  private baseURL: string;

  constructor(baseURL?: string) {
    this.baseURL = baseURL || getApiBaseUrl();

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor pour ajouter le token d'auth
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        if (isDev) {
          console.log('🔑 [API CLIENT] Configuration requête:', {
            url: config.url,
            method: config.method,
            baseURL: config.baseURL
          });
        }

        const token = this.getStoredToken();
        if (token) {
          if (!config.headers) {
            config.headers = new AxiosHeaders();
          }
          config.headers.Authorization = `Bearer ${token}`;
          if (isDev) console.log('🔑 [API CLIENT] Token ajouté aux headers');
        } else {
          if (isDev) console.log('🔑 [API CLIENT] Aucun token à ajouter');
        }

        // Axios gère automatiquement le Content-Type multipart/form-data avec boundary pour FormData
        // Pas besoin de manipulation supplémentaire

        return config;
      },
      (error: any) => {
        console.error('❌ [API CLIENT] Erreur interceptor requête:', error);
        return Promise.reject(error);
      },
    );

    // Response interceptor pour gérer les erreurs
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: any) => {
        const originalRequest = error.config;

        // Handle token refresh on 401
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            await this.refreshToken();
            const token = this.getStoredToken();
            if (token) {
              if (!originalRequest.headers) {
                originalRequest.headers = new AxiosHeaders();
              }
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return this.client(originalRequest);
          } catch (refreshError) {
            this.clearStoredToken();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(this.handleApiError(error));
      },
    );
  }

  private handleApiError(error: any): ApiError {
    if (error.response?.data?.error) {
      return error.response.data.error;
    }

    if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
      return {
        code: 'NETWORK_ERROR',
        message: 'Erreur de connexion réseau',
      };
    }

    if (error.code === 'TIMEOUT' || error.message.includes('timeout')) {
      return {
        code: 'TIMEOUT',
        message: 'Délai d\'attente dépassé',
      };
    }

    return {
      code: 'UNKNOWN_ERROR',
      message: 'Une erreur inattendue s\'est produite',
    };
  }

  private getStoredToken(): string | null {
    const token = localStorage.getItem('auth_token');
    if (isDev) console.log('🔑 [API CLIENT] Token récupéré depuis localStorage:', token ? 'présent' : 'absent');
    return token;
  }

  private setStoredToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  private clearStoredToken(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_info');
  }

  async refreshToken(): Promise<void> {
    try {
      const response = await axios.post(`${this.baseURL}/auth/refresh`, {}, {
        headers: {
          Authorization: `Bearer ${this.getStoredToken()}`,
        },
      });

      const { access_token } = response.data;
      this.setStoredToken(access_token);
    } catch (error) {
      throw new Error('Failed to refresh token');
    }
  }

  setAuthToken(token: string): void {
    this.setStoredToken(token);
  }

  clearAuthToken(): void {
    this.clearStoredToken();
  }

  // Generic HTTP methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    if (isDev) console.log(`📡 GET ${url}`);

    try {
      // Configuration par défaut avec timeout adapté selon l'URL
      const defaultConfig = {
        timeout: this.getTimeoutForUrl(url),
        ...config
      };

      const response: AxiosResponse<T> = await this.client.get(url, defaultConfig);
      if (isDev) {
        console.log(`✅ GET ${url} - Status:`, response.status);
        console.log(`✅ GET ${url} - Headers:`, response.headers);
        console.log(`✅ GET ${url} - Réponse brute:`, response);
        console.log(`✅ GET ${url} - Données:`, response.data);
      }

      // Vérifier si la réponse contient bien des données
      if (!response.data) {
        console.error(`❌ GET ${url} - Pas de données dans la réponse !`);
        throw new Error('Aucune donnée reçue du serveur');
      }

      return response.data;
    } catch (error: any) {
      console.error(`❌ GET ${url} - Erreur complète:`, error);
      console.error(`❌ GET ${url} - Config:`, error.config);
      console.error(`❌ GET ${url} - Réponse d'erreur:`, error.response);
      if (error.response) {
        console.error(`❌ GET ${url} - Status erreur:`, error.response.status);
        console.error(`❌ GET ${url} - Data erreur:`, error.response.data);
        console.error(`❌ GET ${url} - Headers erreur:`, error.response.headers);
      }
      throw error;
    }
  }

  /**
   * Déterminer le timeout approprié selon l'URL de la requête
   */
  private getTimeoutForUrl(url: string): number {
    // Découverte réseau - peut prendre du temps
    if (url.includes('/scanners/discover')) {
      return 90000; // 90 secondes pour la découverte réseau
    }

    // Test IPP - peut nécessiter plus de temps
    if (url.includes('/test-ipp')) {
      return 30000; // 30 secondes pour les tests IPP
    }

    // Scan - peut nécessiter beaucoup de temps
    if (url.includes('/scan')) {
      return 120000; // 2 minutes pour les scans
    }

    // Diagnostic - peut nécessiter plus de temps
    if (url.includes('/diagnose')) {
      return 20000; // 20 secondes pour les diagnostics
    }

    // Requêtes normales - timeout par défaut
    return 30000; // 30 secondes
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    if (isDev) console.log(`📡 POST ${url}`, data ? 'avec données' : 'sans données');

    try {
      // Si FormData, utiliser un timeout plus long pour les uploads
      const isFileUpload = typeof FormData !== 'undefined' && data instanceof FormData;
      const timeout = isFileUpload ? 60000 : this.getTimeoutForUrl(url);
      
      const defaultConfig = {
        timeout,
        ...config,
      };
      if (isDev && isFileUpload) {
        console.log(`📤 [API CLIENT] Upload détecté, timeout: ${timeout}ms`);
      }
      const response: AxiosResponse<T> = await this.client.post(url, data, defaultConfig);
      if (isDev) {
        console.log(`✅ POST ${url} - Status:`, response.status);
        console.log(`✅ POST ${url} - Headers:`, response.headers);
        console.log(`✅ POST ${url} - Réponse brute:`, response);
        console.log(`✅ POST ${url} - Données:`, response.data);
      }

      if (!response.data) {
        console.error(`❌ POST ${url} - Pas de données dans la réponse !`);
        throw new Error('Aucune donnée reçue du serveur');
      }

      return response.data;
    } catch (error: any) {
      console.error(`❌ POST ${url} - Erreur complète:`, error);
      console.error(`❌ POST ${url} - Config:`, error.config);
      console.error(`❌ POST ${url} - Réponse d'erreur:`, error.response);
      if (error.response) {
        console.error(`❌ POST ${url} - Status erreur:`, error.response.status);
        console.error(`❌ POST ${url} - Data erreur:`, error.response.data);
        console.error(`❌ POST ${url} - Headers erreur:`, error.response.headers);
      }
      throw error;
    }
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const defaultConfig = {
      timeout: this.getTimeoutForUrl(url),
      ...config,
    };
    const response: AxiosResponse<T> = await this.client.put(url, data, defaultConfig);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const defaultConfig = {
      timeout: this.getTimeoutForUrl(url),
      ...config,
    };
    const response: AxiosResponse<T> = await this.client.patch(url, data, defaultConfig);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const defaultConfig = {
      timeout: this.getTimeoutForUrl(url),
      ...config,
    };
    const response: AxiosResponse<T> = await this.client.delete(url, defaultConfig);
    return response.data;
  }

  // File upload
  async uploadFile<T = any>(
    url: string,
    file: File,
    additionalData?: Record<string, any>,
  ): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const response: AxiosResponse<T> = await this.client.post(url, formData);

    return response.data;
  }

  // Download file
  async downloadFile(url: string, filename?: string): Promise<void> {
    const response = await this.client.get(url, {
      responseType: 'blob',
    });

    // Récupérer le type MIME à partir des en-têtes de réponse
    const contentType = response.headers['content-type'];
    const blob = new Blob([response.data], { type: contentType });

    const downloadUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
}

// Create and export singleton instance
export const apiClient = new ApiClient();